package us.laantech.admin.menu.clients.ads

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import io.github.vinceglb.filekit.dialogs.FileKitType
import io.github.vinceglb.filekit.dialogs.compose.rememberFilePickerLauncher
import io.github.vinceglb.filekit.name
import io.github.vinceglb.filekit.readBytes
import io.ktor.http.*
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable
import us.laantech.admin.menu.clients.ads.ui.AdPreview
import us.laantech.admin.menu.clients.ads.ui.S3Preview
import us.laantech.admin.menu.clients.ads.ui.SectionPanel
import us.laantech.admin.menu.clients.ads.ui.SequenceCard
import us.laantech.common.dto.company.CompanyBranch
import us.laantech.common.dto.company.PosAd


@Serializable
data class MediaConvertJobResponse(
    val jobId: String?,
    val status: String?,
    val percentComplete: Int?,
    val inputFileName: String?
)

data class AdConfiguratorUiState(
    val companyBranch: CompanyBranch? = null,
    val adFileUrls: List<String> = emptyList(),
    val adSequence: List<PosAd> = emptyList(),
    val mediaConvertJobs: List<MediaConvertJobResponse> = emptyList(),
    val isUploading: Boolean = false,
    val uploadFileName: String? = null,
    val uploadError: String? = null
)

val LocalAdConfigViewModel = compositionLocalOf<AdConfiguratorViewModel> {
    error("No AdConfiguratorViewModel provided")
}



@Composable
fun AdConfiguratorView(
    companyBranch: CompanyBranch?,
    onDismiss: () -> Unit,
    viewModel: AdConfiguratorViewModel = viewModel(
        key = companyBranch?.uuid,
        factory = viewModelFactory {
            initializer { AdConfiguratorViewModel(companyBranch) }
        }
    )
) {

    CompositionLocalProvider(LocalAdConfigViewModel provides viewModel) {

        // TODO into AdConfiguratorView scope
        val uiState by viewModel.uiState.collectAsStateWithLifecycle()
        val scope = rememberCoroutineScope()
        // Opens the OS file dialog; on selection, read the bytes and hand off to the upload flow.
        val launcher = rememberFilePickerLauncher(
            type = FileKitType.File("mp4", "png", "jpg", "jpeg")
        ) { file ->
            file ?: return@rememberFilePickerLauncher
            scope.launch { viewModel.uploadFile(file.name, file.readBytes()) }
        }

        // Load the branch's ad files when the configurator opens.
        LaunchedEffect(Unit) {
            viewModel.fetchAdFiles()
            viewModel.fetchAdSequence()
        }
        // TODO

        Column(Modifier.fillMaxSize().padding(8.dp)) {

            // Top bar: branch label on the left, actions on the right.
            Card(border = BorderStroke(1.dp, Color.Gray)) {
                Row(
                    modifier = Modifier.fillMaxWidth().padding(8.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    Text(
                        text = "Branch: ${companyBranch?.branchName ?: "—"}",
                        modifier = Modifier.weight(1f),
                    )
                    Button(onClick = { viewModel.fetchAdFiles() }) {
                        Text(text = "Refresh")
                    }
                    Button(onClick = { launcher.launch() }) {
                        Text(text = "Upload")
                    }
                    Button(onClick = onDismiss) {
                        Text(text = "Back")
                    }
                    Button(onClick = {
                        viewModel.saveAdConfig()
                    }) {
                        Text(text = "Save")
                    }
                }
            }

            // Files and processing
            Row(modifier = Modifier.fillMaxWidth().weight(1f)) {
                FilesView(modifier = Modifier.weight(4f).fillMaxHeight())
                UploadsView(modifier = Modifier.weight(1f).fillMaxHeight())
            }

            // Sequence editor and preview
            Row(modifier = Modifier.fillMaxWidth().weight(1f)) {
                SequenceEditor(modifier = Modifier.weight(1f).fillMaxHeight())
                AdPreview(modifier = Modifier.weight(2f).fillMaxHeight())
            }
        }

        if (uiState.isUploading) {
            UploadProgressModal(uiState.uploadFileName)
        }
    }

}

@Composable
fun UploadProgressModal(fileName: String?) {
    AlertDialog(
        // No-op: don't let a tap outside cancel an upload that's already in flight.
        onDismissRequest = { },
        confirmButton = { },
        title = { Text(text = "Uploading") },
        text = {
            Column {
                Text(text = fileName ?: "")
                Spacer(Modifier.height(12.dp))
                // Indeterminate: the browser fetch/Js engine can't report upload byte-progress.
                LinearProgressIndicator(modifier = Modifier.fillMaxWidth())
            }
        }
    )
}

// The bare, percent-decoded file name of a presigned S3 URL, e.g.
// ".../my%20ad.mp4?X-Amz-..." -> "my ad.mp4". Ktor's segments are already decoded, matching how
// AdConfiguratorViewModel.insertSegment() derives PosAd.filename. Resolving a stored filename back
// to its URL must compare these decoded names (not url.contains(filename)): the URLs keep spaces
// percent-encoded, so contains() never matches a name with a space, and it also risks matching the
// wrong file when one name is a substring of another.
fun s3FileName(url: String): String = Url(url).segments.lastOrNull().orEmpty()



@Composable
fun FilesView(modifier: Modifier) {
    val uiState by LocalAdConfigViewModel.current.uiState.collectAsStateWithLifecycle()

    SectionPanel(title = "Ad files for given branch (populated from S3)", modifier = modifier) {
        LazyVerticalGrid(
            columns = GridCells.Fixed(4),
            modifier = Modifier.weight(1f).fillMaxWidth(),
        ) {
            items(uiState.adFileUrls) { s3Url ->
                // If image then just show the image
                // If video then show a paused first frame as a thumbnail

                FileCard(s3Url)
            }
        }
    }

}

@Composable
fun FileCard(s3Url: String) {

    val viewModel = LocalAdConfigViewModel.current
    val fileName = Url(s3Url).segments.lastOrNull().orEmpty()

    Card {
        S3Preview(
            s3Url,
            Modifier.fillMaxWidth().aspectRatio(16f / 9f),
            contentScale = ContentScale.Crop,
        )

        Text(text = fileName)

        Row {
            Button({
                viewModel.deleteFile(fileName)
            }) {
                Text(text = "Delete")
            }

            Button({
                viewModel.insertSegment(s3Url)
            }) {
                Text(text = "Insert")
            }
        }
    }

}

@Composable
fun UploadsView(modifier: Modifier) {

    val uiState by LocalAdConfigViewModel.current.uiState.collectAsStateWithLifecycle()

    SectionPanel(title = "Processing (AWS MediaConvert)", modifier = modifier) {
        LazyVerticalGrid(
            columns = GridCells.Fixed(1),
            modifier = Modifier.weight(1f).fillMaxWidth(),
        ) {
            items(uiState.mediaConvertJobs) { mediaConvertJob ->
                UploadCard(mediaConvertJob)
            }
        }
    }

}

@Composable
fun UploadCard(mediaConvertJob: MediaConvertJobResponse) {

    Card(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.padding(8.dp)) {
            Text(text = mediaConvertJob.inputFileName ?: "—")
            Text(text = "Status: ${mediaConvertJob.status ?: "—"}")

            // Determinate: MediaConvert reports percentComplete during conversion.
            val pct = (mediaConvertJob.percentComplete ?: 0).coerceIn(0, 100)
            LinearProgressIndicator(
                progress = { pct / 100f },
                modifier = Modifier.fillMaxWidth()
            )
        }
    }

}

@Composable
fun SequenceEditor(modifier: Modifier = Modifier) {

    val uiState by LocalAdConfigViewModel.current.uiState.collectAsStateWithLifecycle()

    SectionPanel(title = "Sequence", modifier = modifier) {
        // Plain Column (not LazyColumn) so every SequenceCard stays composed and does not
        // reload as it scrolls out of view; verticalScroll makes the fixed set scrollable.
        Column(
            modifier = Modifier
                .weight(1f)
                .fillMaxWidth()
                .verticalScroll(rememberScrollState())
        ) {
            uiState.adSequence.forEachIndexed { index, posAd ->
                SequenceCard(index, posAd)
            }
        }
    }
}



